import { useRef, useMemo, useEffect } from "react";
import { createPortal, useFrame, useThree } from "@react-three/fiber";
import {
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  DoubleSide,
  AdditiveBlending,
  Mesh,
  Vector3,
  Color,
  Group,
} from "three";
import { colors } from "../ui/theme";

const TRAIL_LEN = 28; // longer history for richer ribbon
const DEFAULT_POINT_COUNT = 2;

const VERT = `
  attribute float aAlpha;
  attribute float aSpanAlpha;
  varying float vAlpha;
  varying float vSpanAlpha;
  void main() {
    vAlpha = aAlpha;
    vSpanAlpha = aSpanAlpha;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Speed-adaptive brightness (uSpeed brightens on fast swings) + a
// amber -> gold gradient tied to vAlpha, so the hottest (most recent,
// highest-alpha) part of the ribbon near the blade reads gold and the
// tail cools to amber as it fades — the visible form of zanshin.
const FRAG = `
  uniform float uSpeed;
  uniform vec3 uAmber;
  uniform vec3 uGold;
  varying float vAlpha;
  varying float vSpanAlpha;
  void main() {
    float brightness = 0.68 + clamp(uSpeed / 11.0, 0.0, 0.85);
    vec3 col = mix(uAmber, uGold, vAlpha) * brightness;
    gl_FragColor = vec4(col, vAlpha * vSpanAlpha);
  }
`;

interface BladeTrailProps {
  tipRef?: React.RefObject<Group | null>;
  baseRef?: React.RefObject<Group | null>;
  pointRefs?: React.RefObject<Group | null>[];
  speedRef: React.RefObject<number>;
}

export function BladeTrail({
  tipRef,
  baseRef,
  pointRefs,
  speedRef,
}: BladeTrailProps) {
  const { scene } = useThree();
  const meshRef = useRef<Mesh>(null);
  const refs = pointRefs ?? (tipRef && baseRef ? [tipRef, baseRef] : []);
  const pointCount = Math.max(DEFAULT_POINT_COUNT, refs.length);
  // Fixed-size ring buffer of pre-allocated Vector3s — every frame just
  // copies the new position into the next slot and rotates a write index,
  // instead of allocating+cloning two Vector3s and unshift/truncating a
  // plain array (which also costs an O(n) shift) every single frame.
  const writeIndex = useRef(0);
  const filledCount = useRef(0);

  const { geo, mat, pointHist } = useMemo(() => {
    const maxV = TRAIL_LEN * pointCount;
    const g = new BufferGeometry();
    g.setAttribute(
      "position",
      new BufferAttribute(new Float32Array(maxV * 3), 3),
    );
    g.setAttribute("aAlpha", new BufferAttribute(new Float32Array(maxV), 1));

    const spanAlpha = new Float32Array(maxV);
    for (let i = 0; i < TRAIL_LEN; i++) {
      for (let j = 0; j < pointCount; j++) {
        const fromTip = pointCount > 1 ? j / (pointCount - 1) : 0;
        spanAlpha[i * pointCount + j] = 0.95 - fromTip * 0.38;
      }
    }
    g.setAttribute("aSpanAlpha", new BufferAttribute(spanAlpha, 1));

    const idx: number[] = [];
    for (let i = 0; i < TRAIL_LEN - 1; i++) {
      const row = i * pointCount;
      const nextRow = (i + 1) * pointCount;
      for (let j = 0; j < pointCount - 1; j++) {
        const a = row + j;
        const b = row + j + 1;
        const c = nextRow + j;
        const d = nextRow + j + 1;
        idx.push(a, b, c, b, d, c);
      }
    }
    g.setIndex(idx);

    const m = new ShaderMaterial({
      uniforms: {
        uSpeed: { value: 0 },
        uAmber: { value: new Color(colors.amber) },
        uGold: { value: new Color(colors.gold) },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      // Additive blending is the reason this glows like embers on the dark
      // dōjō background instead of painting an opaque line over it.
      blending: AdditiveBlending,
      side: DoubleSide,
      toneMapped: false,
    });

    const pointHist = Array.from({ length: pointCount }, () =>
      Array.from({ length: TRAIL_LEN }, () => new Vector3()),
    );

    return { geo: g, mat: m, pointHist };
  }, [pointCount]);

  useEffect(
    () => () => {
      geo.dispose();
      mat.dispose();
    },
    [],
  );

  useFrame(() => {
    // Update speed uniform for brightness scaling
    mat.uniforms.uSpeed.value = speedRef.current;

    if (refs.length < pointCount) return;
    for (let j = 0; j < pointCount; j++) {
      if (!refs[j].current) return;
    }

    const idx = writeIndex.current;
    for (let j = 0; j < pointCount; j++) {
      refs[j].current!.getWorldPosition(pointHist[j][idx]);
    }
    filledCount.current = Math.min(filledCount.current + 1, TRAIL_LEN);
    writeIndex.current = (idx + 1) % TRAIL_LEN;

    const n = filledCount.current;
    const posArr = geo.attributes.position.array as Float32Array;
    const alphaArr = geo.attributes.aAlpha.array as Float32Array;

    for (let i = 0; i < TRAIL_LEN; i++) {
      if (i < n) {
        // Steeper fade curve + higher peak opacity. i=0 is the most recent
        // sample; walk backwards through the ring from the last write.
        const histIdx = (writeIndex.current - 1 - i + TRAIL_LEN) % TRAIL_LEN;
        const alpha = Math.pow(1 - i / TRAIL_LEN, 1.1) * 0.86;
        for (let j = 0; j < pointCount; j++) {
          const p = pointHist[j][histIdx];
          const vertIdx = i * pointCount + j;
          const posIdx = vertIdx * 3;
          posArr[posIdx] = p.x;
          posArr[posIdx + 1] = p.y;
          posArr[posIdx + 2] = p.z;
          alphaArr[vertIdx] = alpha;
        }
      } else {
        const startVert = i * pointCount;
        posArr.fill(0, startVert * 3, (startVert + pointCount) * 3);
        alphaArr.fill(0, startVert, startVert + pointCount);
      }
    }

    geo.attributes.position.needsUpdate = true;
    geo.attributes.aAlpha.needsUpdate = true;
  });

  return createPortal(
    <mesh ref={meshRef} geometry={geo} material={mat} frustumCulled={false} />,
    scene,
  );
}
